import apiClient from '@/api/common/axios';
import type { AxiosResponse } from 'axios';
import type { PagedResult, QueryParams } from '@/types/common';
import type {
  CreateOrganizationRequest,
  OrganizationBrandingResponse,
  OrganizationBrandingUploadResponse,
  OrganizationBusinessUnitDto,
  OrganizationDepartmentDto,
  OrganizationDesignationDto,
  OrganizationDetailsResponse,
  OrganizationEmailSettingsResponse,
  OrganizationGradeDto,
  OrganizationListItemDto,
  OrganizationLocationDto,
  UpdateOrganizationBrandingRequest,
  UpdateOrganizationBrandingFiles,
  UpdateOrganizationDetailsRequest,
  UpdateOrganizationEmailSettingsRequest,
  UpsertOrganizationDepartmentRequest,
  UpsertOrganizationDesignationRequest,
  UpsertOrganizationGradeRequest,
  UpsertOrganizationLocationRequest,
  BulkDepartmentRow,
  BulkDesignationRow,
  BulkImportResult,
} from '@/api/organization/organization.types';
import { API_ENDPOINTS } from '@/constants';
import type { BusinessUnitOption } from '@/types/assets/config';

let getAllPromise: Promise<AxiosResponse<OrganizationListItemDto[]>> | null = null;

export const organizationApi = {
  getAll: () => {
    if (!getAllPromise) {
      getAllPromise = apiClient.get<OrganizationListItemDto[]>(API_ENDPOINTS.ORGANIZATION.ALL);
      getAllPromise.finally(() => {
        getAllPromise = null;
      });
    }
    return getAllPromise;
  },
  create: (data: CreateOrganizationRequest) =>
    apiClient.post<OrganizationListItemDto>(API_ENDPOINTS.ORGANIZATION.CREATE, data),
  getDetails: () => apiClient.get<OrganizationDetailsResponse>(API_ENDPOINTS.ORGANIZATION.DETAILS),
  updateDetails: (data: UpdateOrganizationDetailsRequest) =>
    apiClient.put(API_ENDPOINTS.ORGANIZATION.DETAILS, data),
  getEmailSettings: () =>
    apiClient.get<OrganizationEmailSettingsResponse>(API_ENDPOINTS.ORGANIZATION.EMAIL_SETTINGS),
  updateEmailSettings: (data: UpdateOrganizationEmailSettingsRequest) =>
    apiClient.put(API_ENDPOINTS.ORGANIZATION.EMAIL_SETTINGS, data),
  getBySubdomain: (subdomain: string) => apiClient.get(API_ENDPOINTS.ORGANIZATION.GET, { params: { subdomain } }),
  updateGlobal: (data: object) => apiClient.put(API_ENDPOINTS.ORGANIZATION.GLOBAL, data),
  updateIncorporation: (data: object) => apiClient.put(API_ENDPOINTS.ORGANIZATION.INCORPORATION, data),
  getBranding: () => apiClient.get<OrganizationBrandingResponse>(API_ENDPOINTS.ORGANIZATION.BRANDING),
  getUiConfig: () => apiClient.get<OrganizationBrandingResponse>(API_ENDPOINTS.ORGANIZATION.UI_CONFIG),
  updateBranding: (data: UpdateOrganizationBrandingRequest, files?: UpdateOrganizationBrandingFiles) => {
    const fd = new FormData();
    if (data.brandColor != null) fd.append('brandColor', data.brandColor);
    if (data.fontColor != null) fd.append('fontColor', data.fontColor);
    if (data.bodyFontColor != null) fd.append('bodyFontColor', data.bodyFontColor);
    if (data.themeMode != null) fd.append('themeMode', data.themeMode);
    if (data.clearCompanyLogo) fd.append('clearCompanyLogo', 'true');
    if (data.clearFavicon) fd.append('clearFavicon', 'true');
    if (files?.logo) fd.append('logo', files.logo);
    if (files?.favicon) fd.append('favicon', files.favicon);
    return apiClient.post<OrganizationBrandingResponse>(API_ENDPOINTS.ORGANIZATION.BRANDING, fd);
  },
  uploadLogo: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<OrganizationBrandingUploadResponse>(API_ENDPOINTS.ORGANIZATION.LOGO, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  clearLogo: () => apiClient.delete(API_ENDPOINTS.ORGANIZATION.LOGO),
  uploadBrandingFavicon: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<OrganizationBrandingUploadResponse>(
      API_ENDPOINTS.ORGANIZATION.BRANDING_FAVICON,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
  getBusinessUnits: () => apiClient.get<OrganizationBusinessUnitDto[]>(API_ENDPOINTS.ORGANIZATION.BUSINESS_UNITS),
  getDepartments: (params?: QueryParams & { buId?: number }) =>
    apiClient.get<PagedResult<OrganizationDepartmentDto> | OrganizationDepartmentDto[]>(API_ENDPOINTS.ORGANIZATION.DEPARTMENTS, { params }),
  createDepartment: (data: UpsertOrganizationDepartmentRequest) =>
    apiClient.post<OrganizationDepartmentDto>(API_ENDPOINTS.ORGANIZATION.DEPARTMENTS, data),
  updateDepartment: (id: number, data: UpsertOrganizationDepartmentRequest) =>
    apiClient.put<OrganizationDepartmentDto>(API_ENDPOINTS.ORGANIZATION.DEPARTMENT(id), data),
  deleteDepartment: (id: number) => apiClient.delete(API_ENDPOINTS.ORGANIZATION.DEPARTMENT(id)),
  bulkUploadDepartments: (rows: BulkDepartmentRow[]) =>
    apiClient.post<BulkImportResult>(`${API_ENDPOINTS.ORGANIZATION.DEPARTMENTS}/bulk`, rows),
  getDesignations: (params?: QueryParams & { buId?: number; deptId?: number }) =>
    apiClient.get<PagedResult<OrganizationDesignationDto> | OrganizationDesignationDto[]>(API_ENDPOINTS.ORGANIZATION.DESIGNATIONS, { params }),
  createDesignation: (data: UpsertOrganizationDesignationRequest) =>
    apiClient.post<OrganizationDesignationDto>(API_ENDPOINTS.ORGANIZATION.DESIGNATIONS, data),
  updateDesignation: (id: number, data: UpsertOrganizationDesignationRequest) =>
    apiClient.put<OrganizationDesignationDto>(API_ENDPOINTS.ORGANIZATION.DESIGNATION(id), data),
  deleteDesignation: (id: number) => apiClient.delete(API_ENDPOINTS.ORGANIZATION.DESIGNATION(id)),
  bulkUploadDesignations: (rows: BulkDesignationRow[]) =>
    apiClient.post<BulkImportResult>(`${API_ENDPOINTS.ORGANIZATION.DESIGNATIONS}/bulk`, rows),
  getGrades: (params?: QueryParams & { buId?: number }) =>
    apiClient.get<PagedResult<OrganizationGradeDto> | OrganizationGradeDto[]>(API_ENDPOINTS.ORGANIZATION.GRADES, { params }),
  createGrade: (data: UpsertOrganizationGradeRequest) =>
    apiClient.post<OrganizationGradeDto>(API_ENDPOINTS.ORGANIZATION.GRADES, data),
  updateGrade: (id: number, data: UpsertOrganizationGradeRequest) =>
    apiClient.put<OrganizationGradeDto>(API_ENDPOINTS.ORGANIZATION.GRADE(id), data),
  deleteGrade: (id: number) => apiClient.delete(API_ENDPOINTS.ORGANIZATION.GRADE(id)),
  getLocations: () => apiClient.get<OrganizationLocationDto[]>(API_ENDPOINTS.ORGANIZATION.LOCATIONS),
  createLocation: (data: UpsertOrganizationLocationRequest) =>
    apiClient.post<OrganizationLocationDto>(API_ENDPOINTS.ORGANIZATION.LOCATIONS, data),
  updateLocation: (id: number, data: UpsertOrganizationLocationRequest) =>
    apiClient.put<OrganizationLocationDto>(API_ENDPOINTS.ORGANIZATION.LOCATION(id), data),
  deleteLocation: (id: number) => apiClient.delete(API_ENDPOINTS.ORGANIZATION.LOCATION(id)),
};
